import React, { useState, useEffect } from 'react';

const TargetedDebugOverlay = ({ 
  isVisible = false, 
  targetElements = [],
  showLabels = true 
}) => {
  const [elements, setElements] = useState([]);

  useEffect(() => {
    if (!isVisible || targetElements.length === 0) return;

    const highlightTargetElements = () => {
      const foundElements = [];

      targetElements.forEach(target => {
        let element = null;
        
        // Try different ways to find the element(s)
        if (target.id) {
          element = document.getElementById(target.id);
        } else if (target.className) {
          // Handle multiple classes - find ALL elements with this class
          const classes = target.className.split(' ');
          if (classes.length === 1) {
            const allElements = document.querySelectorAll(`.${target.className}`);
            // Return all elements, not just the first one
            if (allElements.length > 0) {
              // Create multiple entries for each element found
              allElements.forEach((el, index) => {
                const rect = el.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(el);
                
                foundElements.push({
                  id: `${target.id || target.name || 'unknown'}_${index}`,
                  name: `${target.name} ${index + 1}`,
                  className: target.className || '',
                  tagName: el.tagName,
                  rect: {
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height
                  },
                  position: computedStyle.position,
                  zIndex: computedStyle.zIndex,
                  backgroundColor: computedStyle.backgroundColor,
                  border: computedStyle.border
                });
              });
              return; // Skip the single element processing below
            }
          } else {
            element = document.querySelector(`.${classes.join('.')}`);
          }
        } else if (target.selector) {
          // Handle CSS selectors - find ALL elements with this selector
          const allElements = document.querySelectorAll(target.selector);
          if (allElements.length > 0) {
            // Create multiple entries for each element found
            allElements.forEach((el, index) => {
              const rect = el.getBoundingClientRect();
              const computedStyle = window.getComputedStyle(el);
              
              foundElements.push({
                id: `${target.id || target.name || 'unknown'}_${index}`,
                name: `${target.name} ${index + 1}`,
                className: target.className || '',
                tagName: el.tagName,
                rect: {
                  x: rect.x,
                  y: rect.y,
                  width: rect.width,
                  height: rect.height
                },
                position: computedStyle.position,
                zIndex: computedStyle.zIndex,
                backgroundColor: computedStyle.backgroundColor,
                border: computedStyle.border
              });
            });
            return; // Skip the single element processing below
          }
        } else if (target.tagName) {
          // Try to find by tag name and other properties
          const elements = document.querySelectorAll(target.tagName.toLowerCase());
          for (let el of elements) {
            if (target.className && el.className === target.className) {
              element = el;
              break;
            }
            if (target.textContent && el.textContent?.includes(target.textContent)) {
              element = el;
              break;
            }
          }
        }
        
        // If we have position info, use it to find the exact element
        if (!element && target.position) {
          const elements = document.querySelectorAll(target.tagName?.toLowerCase() || '*');
          for (let el of elements) {
            const rect = el.getBoundingClientRect();
            if (Math.abs(rect.x - target.position.x) < 5 && 
                Math.abs(rect.y - target.position.y) < 5) {
              element = el;
              break;
            }
          }
        }
        
        // If we have a unique path, use it to find the exact element
        if (!element && target.uniquePath) {
          try {
            element = document.querySelector(target.uniquePath);
          } catch (e) {
            console.warn('Invalid selector:', target.uniquePath);
          }
        }

        if (element) {
          const rect = element.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(element);
          
          foundElements.push({
            id: target.id || target.name || 'unknown',
            name: target.name || target.id || 'unnamed',
            className: target.className || '',
            tagName: element.tagName,
            rect: {
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height
            },
            position: computedStyle.position,
            zIndex: computedStyle.zIndex,
            backgroundColor: computedStyle.backgroundColor,
            border: computedStyle.border
          });
        } else {
          console.warn(`TargetedDebugOverlay: Element not found for target:`, target);
          console.log('Available elements with class "general-status":', document.querySelectorAll('.general-status'));
          console.log('Available elements with class "circle red":', document.querySelectorAll('.circle.red'));
          console.log('Available elements with class "circle blue":', document.querySelectorAll('.circle.blue'));
          
          // Debug drone selectors
          if (target.selector) {
            console.log(`Trying selector: ${target.selector}`);
            const testElements = document.querySelectorAll(target.selector);
            console.log(`Found ${testElements.length} elements with selector:`, testElements);
          }
        }
      });

      setElements(foundElements);
    };

    highlightTargetElements();
    
    // Update every 500ms to catch dynamic changes
    const interval = setInterval(highlightTargetElements, 500);
    return () => clearInterval(interval);
  }, [isVisible, targetElements]);

  if (!isVisible || elements.length === 0) return null;

  console.log('TargetedDebugOverlay: Rendering', elements.length, 'targeted elements');
  console.log('Found elements:', elements.map(el => ({ name: el.name, className: el.className, tagName: el.tagName })));

  return (
    <div
      className="targeted-debug-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 10001, // Higher z-index to ensure visibility
        opacity: 1
      }}
    >
      {elements.map((element, index) => {
        console.log(`Rendering boundary for ${element.name} at position:`, element.rect);
        return (
          <div
            key={element.id}
            style={{
              position: 'absolute',
              left: element.rect.x,
              top: element.rect.y,
              width: element.rect.width,
              height: element.rect.height,
              border: '4px solid #ffff00', // Thicker yellow border
              backgroundColor: 'rgba(0, 255, 0, 0.2)', // More visible background
              pointerEvents: 'none',
              fontSize: '12px',
              color: '#ffff00',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: '4px',
              overflow: 'hidden',
              boxSizing: 'border-box',
              fontWeight: 'bold',
              zIndex: 10002 // Even higher z-index for individual boundaries
            }}
          >
            {showLabels && (
              <div style={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                color: '#ffff00', 
                padding: '2px 6px',
                fontSize: '10px',
                borderRadius: '3px',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                border: '1px solid #ffff00'
              }}>
                {element.name}
                {element.className && ` (${element.className})`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TargetedDebugOverlay;
